import { Alert, Box, Button, ButtonGroup, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, InputLabel, ListItemText, Menu, MenuItem, Select, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import IAlert from "../../../types/Alert";
import PacientesUtils from "../../../utils/Pacientes";
import { IFacturaCompleta } from "../facturarCita/DetalleFacturaComplementaria";
import IPaciente from "../../../types/Paciente";
import * as constantes from "../../../common/Constantes";
import FacturacionService from "../../../services/FacturacionService";
import ILog from "../../../types/Log";
import DownloadIcon from '@mui/icons-material/GetApp';
import EmailIcon from '@mui/icons-material/Email';
import MoreIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import LogService from "../../../services/LogService";
import DocUtils from "../../../utils/Doc";
import ConfigUtils from "../../../utils/Config";
import moment from "moment";
import React from "react";
import { IConcepto } from "../facturarCita/DetalleFactura";
import { IFacturaMetodoPago } from "../facturarCita/FacturarCitaDialog";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import EditarFacturaManualForm from "./EditarFacturaManualForm";
import RectificarFacturaManual from "./RectificarFacturaManual";
import { formatCurrency, formatNumeroFactura } from "../../../utils/Factura";
import CambiarEntidadDialog from "./CambiarEntidadDialog";
import { getFacturaAcciones } from "./facturaAcciones";
import NumeroOrientativoEmision, { cargarNumeroOrientativo, InfoNumeroOrientativo } from "./NumeroOrientativoEmision";
import AnularFacturaDialog from "./AnularFacturaDialog";
import { chipEstadoFactura, chipEstadoPago } from "./chipsEstadoFactura";
import EliminarFacturaAnulada, { AvisoEliminacion } from "./EliminarFacturaAnulada";
import EnlaceFactura from "./EnlaceFactura";

interface DetalleFacturaManualProps {
  open: boolean;
  onClose: () => void;
  facturaId: string;
  onEdit?: (factura: IFacturaCompleta) => void;
  onFacturaPagada?: (id: string, fechaPago: string | null | undefined, estado: string, estadoPago: string) => void;
  onFacturaEmitida?: (id: string, estado: string, estadoPago: string, numero?: string) => void;
  onFacturaRectificada?: (factura: IFacturaCompleta) => void;
  onAnularFactura?: (_id: string, motivoAnulacion: string, anulacion?: IFacturaCompleta) => void;
  onFacturaEliminada?: (_id: string, aviso: AvisoEliminacion) => void;
  // La factura había cambiado de estado en otra pantalla: se ha vuelto a cargar y se informa de su estado actual.
  onEstadoCambiado?: (factura: IFacturaCompleta) => void;
  onEntidadCambiada?: (facturaAnuladaId: string, facturaNueva: IFacturaCompleta, anulacion?: IFacturaCompleta | null) => void;
}

const DetalleFacturaManual = ({ open, onClose, facturaId, onEdit, onFacturaPagada, onFacturaEmitida, onFacturaRectificada, onAnularFactura, onEntidadCambiada, onFacturaEliminada, onEstadoCambiado }: DetalleFacturaManualProps) => {
    const history = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
    const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });
    
    const [showMissingAlert, setShowMissingAlert] = useState(true);
    const [loading, setLoading] = useState(true);
    const [factura, setFactura] = useState<IFacturaCompleta | null>(null);
    const monedaFactura = factura?.moneda || 'EUR';
    const acciones = getFacturaAcciones({ ...(factura ?? {}), tieneOpcionAnular: true });
    const [paciente, setPaciente] = useState<IPaciente | null>(null);

    const [expandirDetalle, setExpandirDetalle] = useState(false);
    const [loadingEnviar, setLoadingEnviar] = useState(false);
    const [modalPagar, setModalPagar] = useState(false);
    const [loadingPagar, setLoadingPagar] = useState(false);
    const [metodoPago, setMetodoPago] = useState<IFacturaMetodoPago | undefined>(undefined);
    const [errorMetodoPago, setErrorMetodoPago] = useState<string | undefined>(undefined);
    const [errorFechaPago, setErrorFechaPago] = useState<string | undefined>(undefined);
    const [loadingEmitir, setLoadingEmitir] = useState(false);
    const [modalEmitir, setModalEmitir] = useState(false);
    const [numeroOrientativo, setNumeroOrientativo] = useState<InfoNumeroOrientativo | null>(null);
    const [erroresPaciente, setErroresPaciente] = useState<string[]>([]);
    const [modalEditarFactura, setModalEditarFactura] = useState(false);
    const [facturaRectificar, setFacturaRectificar] = useState<IFacturaCompleta | null>(null);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [modalAnular, setModalAnular] = useState(false);
    const [loadingAnular, setLoadingAnular] = useState(false);
    const [motivoAnulacion, setMotivoAnulacion] = useState("");
    const [confirmarAnulacion, setConfirmarAnulacion] = useState(false);

    const [modalCambiarEntidad, setModalCambiarEntidad] = useState(false);
    const [loadingCambiarEntidad, setLoadingCambiarEntidad] = useState(false);

    const metodosPago = ConfigUtils.getMetodosPago();
    const userRole: number = ConfigUtils.getUserRole();

    useEffect(() => {
      load();
    }, []);

    const load = async () => {
        try {
          setLoading(true);
          if (!facturaId) return;
          const response = await FacturacionService.getFacturaManualById(facturaId);
          setFactura(response.data.factura);
          setPaciente(response.data.paciente);
      }  catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "DetalleFacturaManual.Component.load",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoading(false);
      }
    };

    const openModalPagar = () => {
      if (factura?.estadoPago === "Pagada" && factura.metodoPago) {
        setMetodoPago({
          nombre: factura.metodoPago.nombre,
          codigo: factura.metodoPago.codigo,
          fechaPago: moment(factura.metodoPago.fechaPago),
        });
      }
      setModalPagar(true);
    };

    const openModalEmitir = async () => {
      const errores: string[] = [];

      if (factura && !factura.receptor?.nombre) errores.push("Nombre");
      if (factura && !factura.receptor?.nif) errores.push("DNI");
      if (!paciente?.fechaNacimiento) errores.push("Fecha de nacimiento");

      if (errores.length > 0) {
        setErroresPaciente(errores);
      }
      // El número orientativo se pide antes de abrir el modal para que aparezca completo, sin saltos.
      setNumeroOrientativo(errores.length === 0 ? await cargarNumeroOrientativo(factura?._id) : null);
      setModalEmitir(true);
    };

    const closeModalPagar = () => {
      setMetodoPago(undefined);
      setErrorMetodoPago(undefined);
      setErrorFechaPago(undefined);
      setModalPagar(false);
    };

    const closeModalEmitir = () => {
      setErroresPaciente([]);
      setModalEmitir(false);
    };

    const pagarFactura = async () => {
      let hasError = false;

      if (!metodoPago) {
        setErrorMetodoPago("Debes seleccionar un método de pago");
        hasError = true;
      }
      if (!metodoPago?.fechaPago) {
        setErrorFechaPago("Debes seleccionar una fecha de pago");
        hasError = true;
      } else if (!moment.isMoment(metodoPago.fechaPago) || !metodoPago.fechaPago.isValid()) {
        setErrorFechaPago("Debes seleccionar una fecha de pago válida");
        hasError = true;
      }

      if (hasError) return;
      if (!metodoPago) return;

      setErrorMetodoPago(undefined);
      setErrorFechaPago(undefined);

      try {
        setLoadingPagar(true);
        const response = await FacturacionService.pagarFactura(factura?._id!, { 
          tipoPago: "manual",
          metodoPago });

        setFactura(prev => ({
          ...prev!,
          estadoPago: "Pagada",
          metodoPago: metodoPago,
        }));

        onFacturaPagada?.(response.data.factura._id, response.data.factura.metodoPago?.fechaPago?.toString() ?? null, response.data.factura.estado, response.data.factura.estadoPago);
        closeModalPagar();

      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "DetalleFacturaManual.Component.pagarFactura",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingPagar(false);
      }
    };

    const emitirFactura = async () => {
      try {
        setLoadingEmitir(true);
        const response = await FacturacionService.emitirFactura(factura?._id!, { 
          userId: ConfigUtils.getUserId(),
          tipoPago: "manual" });

        setFactura(prev => ({
          ...prev!,
          estado: "Emitida",
          numero: response.data.factura.numero,
          fechaEmision: response.data.factura.fechaEmision,
        }));

        onFacturaEmitida?.(response.data.factura._id, response.data.factura.estado, response.data.factura.estadoPago, response.data.factura.numero);
        closeModalEmitir();

      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "DetalleFacturaManual.Component.emitirFactura",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingEmitir(false);
      }
    };

    const handleDownloadFactura = () => {
      DocUtils.formatFactura(factura, true, "total", undefined, undefined, esMenor ? nombreCompletoPaciente : undefined);
    };

    const handleEnviarFactura = async () => {
      try {
        setLoadingEnviar(true);
        const facturaPDF = DocUtils.formatFactura(factura, false, "total", undefined, undefined, esMenor ? nombreCompletoPaciente : undefined);
        const response = await FacturacionService.sendFactura({ facturaId: factura?._id, email: paciente?.email, doc: facturaPDF, nombre: factura?.receptor.nombre, numeroFactura: factura?.numero });
        setAlert({ open: true, severity: 'success', msg: response.data.message });
      } catch (e: any) {
        if (e.response.status === 401) {
              localStorage.clear();
              history(constantes.R_HOME);
          } else {
              const error: ILog = { method: 'DetalleFacturaManual.Component.sendFactura', level: 'error', message: e.message, meta: e.response };
              LogService.logError(error);
              setAlert({ open: true, severity: 'error', msg: e.response.data.message });
          }
      } finally {
        setLoadingEnviar(false);
      }
    };

    // Otra pantalla cambió la factura mientras esta seguía abierta: se vuelven a pedir sus datos y se muestran actualizados.
    const recargarPorCambioDeEstado = async (mensaje: string) => {
      try {
        const response = await FacturacionService.getFacturaManualById(factura?._id ?? facturaId);
        setFactura(response.data.factura);
        setPaciente(response.data.paciente);
        onEstadoCambiado?.(response.data.factura);
        setAlert({ open: true, severity: 'warning', msg: mensaje });
      } catch (e: any) {
        setAlert({ open: true, severity: 'error', msg: e.response?.data?.message || e.message });
      }
    };

    const handleAnularFactura = async () => {
      try {
        setLoadingAnular(true);
        const response = await FacturacionService.anularFactura(factura?._id!, { motivoAnulacion, tipoPago: "total", estadoEsperado: factura?.estado });
        setAlert({ open: true, severity: 'success', msg: response.data.message });
        onAnularFactura?.(factura?._id!, motivoAnulacion, response.data.factura);
        setFactura(prev => {
          if (!prev) return prev;
          return {
            ...factura,
            estado: "Anulada",
            motivoAnulacion: motivoAnulacion
          } as IFacturaCompleta
        });
        setModalAnular(false);
        setMotivoAnulacion("");
      } catch (e: any) {
        if (e.response?.status === 409) {
          setModalAnular(false);
          setMotivoAnulacion("");
          await recargarPorCambioDeEstado(e.response.data.message);
        } else if (e.response.status === 401) {
              localStorage.clear();
              history(constantes.R_HOME);
          } else {
              const error: ILog = { method: 'DetalleFacturaManual.Component.anularFactura', level: 'error', message: e.message, meta: e.response };
              LogService.logError(error);
              setAlert({ open: true, severity: 'error', msg: e.response.data.message });
          }
      } finally {
        setLoadingAnular(false);
      }
    };

    const handleCambiarEntidad = async (nuevaEntidadId: string | null) => {
      try {
        setLoadingCambiarEntidad(true);
        const response = await FacturacionService.cambiarEntidadFactura(factura!._id, {
          userId: ConfigUtils.getUserId(),
          entidadNuevaId: nuevaEntidadId,
          estadoEsperado: factura!.estado,
        });
        // Se sustituye por la factura nueva completa (no se fusiona): no trae número ni fecha de emisión, y al fusionar
        // se conservarían los de la factura anulada.
        const facturaNueva = response.data.factura as IFacturaCompleta;
        onEntidadCambiada?.(factura!._id, facturaNueva, response.data.anulacion);
        setFactura(facturaNueva);
        setModalCambiarEntidad(false);
        setAlert({ open: true, severity: 'success', msg: 'Entidad de facturación cambiada correctamente' });
      } catch (e: any) {
        if (e.response?.status === 409) {
          setModalCambiarEntidad(false);
          await recargarPorCambioDeEstado(e.response.data.message);
        } else if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = { method: 'DetalleFacturaManual.Component.cambiarEntidad', level: 'error', message: e.message, meta: e.response };
          LogService.logError(error);
          setAlert({ open: true, severity: 'error', msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingCambiarEntidad(false);
      }
    };

    const edad = paciente?.fechaNacimiento
      ? PacientesUtils.calcularEdad(paciente?.fechaNacimiento)
      : null;
    const esMenor = edad !== null && edad !== undefined && edad < 18;
    const nombreCompletoPaciente = `${paciente?.nombre} ${paciente?.apellidos ?? ""}`.trim();
    const pacienteMenor = esMenor ? nombreCompletoPaciente : undefined;
    const missingFields = [
        !paciente?.domicilio ? "Dirección" : null,
        !paciente?.fechaNacimiento ? "Fecha de Nacimiento" : null,

        esMenor
          ? !paciente?.DNItutor
            ? "DNI del tutor"
            : null
          : !paciente?.DNI
            ? "DNI"
            : null,
    ].filter((f): f is string => Boolean(f));

    const receptor = factura?.receptor ?? {
      nombre: paciente?.nombre,
      email: paciente?.email,
      direccion: paciente?.domicilio,
      nif: esMenor ? paciente?.DNItutor : paciente?.DNI
    };

    const { isDisabledEmail, tooltipMsg } = PacientesUtils.canSendFactura(
      loadingEnviar,
      paciente?.authEmail,
      paciente?.email,
      factura?.estado,
      factura?.facturaRectificadaId
    );

    return (
        <>
            <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
                {loading ? <CircularProgress style={{ margin: "20px auto" }} /> : 
                <>
                    <Box sx={{ p: 2, backgroundColor: "#f8f9fa", border: "1px solid #e5e5e5" }}>
                       {/* Aviso de datos incompletos */}
                        {missingFields.length > 0 && showMissingAlert && factura && factura.estado !== "Emitida" && (
                        <Alert
                          severity="warning"
                          sx={{ mb: 1, fontSize: "0.875rem" }}
                          onClose={() => setShowMissingAlert(false)}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">
                              Faltan datos importantes: <b>{missingFields.join(", ")}</b>.
                            </Typography>
                        
                            <Button
                              size="small"
                              color="warning"
                              onClick={() => history(constantes.R_NUEVOPACIENTE, { state: paciente })}
                            >
                              Editar
                            </Button>
                          </Box>
                        </Alert>
                        )}
                        {/* Emisor — solo si hay más de una entidad de facturación configurada */}
                        {factura?.emisor && ConfigUtils.getDatosFacturacionAdicional().length > 0 && (
                          <Box display="flex" alignItems="center" gap={0.5} mb={1.5} pb={1} sx={{ borderBottom: "1px solid #e5e5e5" }}>
                            <Typography variant="body2" color="text.secondary">
                              <b>Entidad emisora:</b> {factura.emisor.razonSocial}
                            </Typography>
                            {userRole === constantes.ROLES.ADMINISTRADOR && factura.estado !== 'Anulada' && !factura.facturaOriginalId && !factura.facturaRectificadaId && !factura.bonoId && (
                              <Tooltip title="Cambiar entidad de facturación">
                                <IconButton size="small" onClick={() => setModalCambiarEntidad(true)}>
                                  <EditIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}

                        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between">
                            {/* Datos del receptor */}
                            <Box flex={1}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                Receptor
                              </Typography>
                              {pacienteMenor && <Typography variant="body2">
                                <b>Paciente:</b> {pacienteMenor}
                              </Typography>}
                              <Typography variant="body2">
                                <b>Nombre:</b> {receptor.nombre}
                              </Typography>
                              {receptor.nif && (
                                <Typography variant="body2">
                                  <b>DNI:</b> {receptor.nif}
                                </Typography>
                              )}
                              {receptor.email && (
                                <Typography variant="body2">
                                  <b>Email:</b> {receptor.email}
                                </Typography>
                              )}
                              {receptor.direccion && (
                                <Typography variant="body2">
                                  <b>Domicilio:</b> {receptor.direccion}
                                </Typography>
                              )}
                            </Box>
                        </Box>
                    </Box>

                    {factura && <DialogContent>
                        <Box mb={isMobile ? 2 : 3}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            gap={2}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600 }}
                            >
                              Información de la factura
                            </Typography>       
                            {/* Acciones */}
                            <Box display="flex" gap={0.5}>
                              <Tooltip title="Descargar factura">
                                <IconButton
                                  size="small"
                                  onClick={handleDownloadFactura}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={factura.estado === "Anulada" ? "Factura anulada" : tooltipMsg}> 
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={handleEnviarFactura}
                                    disabled={isDisabledEmail || factura.estado === "Anulada"}
                                  >
                                    {loadingEnviar ? <CircularProgress size={18} /> : <EmailIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </Box>        
                                
                          <Box mb={1}>
                            {/* --- Fila principal: Nº, Fecha, Estado --- */}
                            <Stack
                              direction={isMobile ? "column" : "row"}
                              justifyContent="flex-start"
                              alignItems={isMobile ? "flex-start" : "center"}
                              spacing={isMobile ? 1 : 2}
                            >
                              {/* Nº */}
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Nº
                                </Typography>
                                <Typography variant="body2">{formatNumeroFactura(factura.numero)}</Typography>
                              </Stack>

                              {/* Fecha */}
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Fecha
                                </Typography>
                                <Typography variant="body2">{new Date(factura.fechaFactura!).toLocaleDateString()}</Typography>
                              </Stack>

                              {/* Estado */}
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 140 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Estado
                                </Typography>
                                {factura.estado === "Anulada" ? (
                                  <Tooltip
                                    title={factura?.motivoAnulacion}
                                    placement="top"
                                    arrow
                                  >
                                    <Chip
                                      label={factura.estado}
                                      size="small"
                                      sx={{ textTransform: "capitalize", border: `1px solid #d32f2f`, color: "#d32f2f" }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Chip
                                    {...chipEstadoFactura(factura)}
                                    size="small"
                                  />
                                )}
                              </Stack>
                            </Stack>

                            {/* --- Fila final: Información de rectificación --- */}
                            {(factura.facturaOriginalId || factura.facturaRectificadaId) && (
                              <Stack direction="column" spacing={0.5} sx={{ mt: 0.5 }}>
                                {factura.facturaOriginalId && (
                                  <Stack direction="row" spacing={1}>
                                    <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                                      {factura?.tipoRectificacion === "anulacion" ? "Anulación de la factura nº" : "Rectificativa de la factura nº"}
                                    </Typography>
                                    <EnlaceFactura facturaId={factura.facturaOriginalRef} numero={factura.facturaOriginalId} cursiva />
                                  </Stack>
                                )}
                                {factura.facturaRectificadaId && (
                                  <Stack direction="row" spacing={1}>
                                    <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                                      {factura?.estado === "Anulada" ? "Anulada por la factura nº" : "Rectificada por la factura nº"}
                                    </Typography>
                                    <EnlaceFactura facturaId={factura.facturaRectificadaId} numero={factura.facturaRectificada} cursiva />
                                  </Stack>
                                )}
                              </Stack>
                            )}
                          </Box>

                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Box
                          display="flex"
                          flexDirection={isMobile ? "column" : "row"}
                          justifyContent="space-between"
                          alignItems={isMobile ? "stretch" : "center"}
                          mb={2}
                          gap={isMobile ? 1.5 : 4}
                        >
                          {/* INFO IZQUIERDA */}
                          <Box display="flex" flexDirection="column" gap={isMobile ? 0.75 : 1}>
                            {/* Estado de pago */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" gap={{ xs: 0, sm: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Estado
                              </Typography>
                              <Chip
                                {...chipEstadoPago(factura)}
                                size="small"
                              />
                            </Box>
                        
                            {/* Tipo de facturación */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" gap={{ xs: 0, sm: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Tipo
                              </Typography>
                              <Chip
                                label={"Total"}
                                size="small"
                                color="info"
                              />
                            </Box>
                        
                            {/* Servicio */}
                            <Box display="flex" justifyContent="space-between" gap={{ xs: 0, sm: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Servicio
                              </Typography>
                              <Typography variant="body2" noWrap>
                                {factura.servicio}
                              </Typography>
                            </Box>
                          </Box>
                        
                          {/* IMPORTE */}
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems={isMobile ? "flex-start" : "flex-end"}
                            mt={isMobile ? 1 : 0}
                          >
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              Importe
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                              {formatCurrency(Number(factura.importeTotal), monedaFactura)}
                            </Typography>
                          </Box>
                        </Box>
                        {/* Si la factura está pagada, método de pago */}
                        {factura.estadoPago === "Pagada" && (
                        <Box
                          mb={3}
                          p={2}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            backgroundColor: "background.paper",
                            maxWidth: 400,
                          }}
                        >
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Typography variant="body2">
                              <b>Método de pago:</b> {factura.metodoPago?.nombre}
                            </Typography>
                            <Typography variant="body2">
                              <b>Fecha de pago:</b> {moment(factura.metodoPago?.fechaPago).format('DD/MM/YYYY')}
                            </Typography>
                          </Box>
                        </Box>
                        )}
                        <Divider />
                        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => setExpandirDetalle(!expandirDetalle)}
                          >
                            {expandirDetalle ? "Ocultar detalles" : "Ver detalles"}
                          </Button>    

                          <Box display="flex" gap={1} alignItems="center">  

                          {acciones.mostrarBotonesAccion && (
                            <ButtonGroup variant="outlined" color="success">
                              {acciones.mostrarPagar && (
                                <Button variant={acciones.variantPagar} onClick={openModalPagar}>Pagar</Button>
                              )}
                              {acciones.mostrarEmitir && (
                                <Button variant={acciones.variantEmitir} onClick={openModalEmitir}>Emitir</Button>
                              )}
                            </ButtonGroup>
                          )}

                          <EliminarFacturaAnulada factura={factura} onEliminada={(aviso) => { onFacturaEliminada?.(factura!._id, aviso); onClose(); }} />
                          {userRole === constantes.ROLES.ADMINISTRADOR &&
                            acciones.mostrarMenuAcciones && (
                              <>
                                <Tooltip title="Más acciones">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => setAnchorEl(e.currentTarget)}
                                  >
                                    <MoreIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                <Menu
                                  anchorEl={anchorEl}
                                  open={Boolean(anchorEl)}
                                  onClose={() => setAnchorEl(null)}
                                >
                                  {acciones.mostrarCorregirCobro && <MenuItem
                                    onClick={() => {
                                      setAnchorEl(null);
                                      openModalPagar();
                                    }}
                                  >
                                    <ListItemText>
                                      Corregir cobro
                                    </ListItemText>
                                  </MenuItem>}
                                  {acciones.mostrarRectificar && <MenuItem
                                    onClick={() => {
                                      setAnchorEl(null);
                                      setFacturaRectificar(factura);
                                    }}
                                  >
                                    <ListItemText>
                                      Rectificar factura
                                    </ListItemText>
                                  </MenuItem>}

                                    <MenuItem
                                      key="menu-anular"
                                      onClick={() => {
                                        setAnchorEl(null);
                                        setModalAnular(true);
                                      }}
                                    >
                                      <ListItemText>
                                        Anular factura
                                      </ListItemText>
                                    </MenuItem>
                                </Menu>
                              </>
                          )}
                          </Box>
                        </Box>
                        {/* Bloque expandible */}
                        <Collapse in={expandirDetalle} timeout="auto" unmountOnExit>
                          <Box
                            mt={2}
                            p={2}
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 2,
                              backgroundColor: "background.paper",
                              overflowX: "auto",
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                              Detalle de conceptos
                            </Typography>
                        
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Concepto</TableCell>
                                  <TableCell align="right">Cantidad</TableCell>
                                  <TableCell align="right">Precio unit.</TableCell>
                                  <TableCell align="right">Subtotal</TableCell>
                                </TableRow>
                              </TableHead>
                        
                              <TableBody>
                                {factura.conceptos.map((c: IConcepto, index: number) => {
                                  const imp = factura.impuestos?.[index];
                                  const base = c.cantidad * c.precio;
                                  const cuota = imp ? base * (imp.cuota / 100) : 0;
                                  const total = base + cuota;
                                
                                  return (
                                    <React.Fragment key={index}>
                                      {/* LÍNEA PRINCIPAL */}
                                      <TableRow hover>
                                        <TableCell>{c.nombre}</TableCell>
                                        <TableCell align="right">{c.cantidad}</TableCell>
                                        <TableCell align="right">
                                          {formatCurrency(c.precio, monedaFactura)}
                                        </TableCell>
                                        <TableCell align="right">
                                          {formatCurrency(base, monedaFactura)}
                                        </TableCell>
                                      </TableRow>
                                    
                                      {/* LÍNEA DETALLE: IMPUESTO + TOTAL */}
                                      {imp && (
                                        <TableRow>
                                          <TableCell
                                            colSpan={4}
                                            sx={{
                                              pl: 6,
                                              py: 0.8,
                                              backgroundColor: "action.hover",
                                              borderBottom: "none",
                                            }}
                                          >
                                            <Typography variant="body2" color="text.secondary">
                                              {imp.tipo} ({imp.cuota}%): {formatCurrency(cuota, monedaFactura)}
                                              &nbsp;•&nbsp;
                                              <strong>Total: {formatCurrency(total, monedaFactura)}</strong>
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                    </DialogContent>}

                    <DialogActions>
                      <Button onClick={onClose}>
                        Cerrar
                      </Button>
                      {factura?.estado !== "Emitida" && 
                      <Button 
                        size="small" 
                        color="primary" 
                        variant="contained" 
                        onClick={() => setModalEditarFactura(true)}
                        disabled={factura?.estado === "Anulada"}
                      >
                        {factura ? "Editar" : "Facturar"}
                      </Button>}  
                    </DialogActions>
                </>}        
            </Dialog>

            {/* Modal editar factura manual */}
            {modalEditarFactura && 
            <EditarFacturaManualForm 
              open={modalEditarFactura}
              onClose={() => setModalEditarFactura(false)}
              factura={factura!}
              paciente={paciente}
              onSuccess={(updatedFactura) => {
                setFactura(updatedFactura);
                setModalEditarFactura(false);
                setAlert({ open: true, severity: "success", msg: "Factura actualizada correctamente" });
                onEdit?.(updatedFactura);
              }}
            />}

            {/* Modal pagar */}
            {modalPagar &&
            <Dialog open={modalPagar} onClose={loadingPagar ? undefined : closeModalPagar} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: "bold" }}>
                {factura?.estadoPago === "Pagada" ? "Corregir cobro" : "Pagar factura"}
              </DialogTitle>
              <DialogContent sx={{ pt: 1 }}>
                {/* Importe total */}
                <Box
                  sx={{
                    mb: 3,
                    mt: 1,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "grey.100",
                    textAlign: "center"
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: "bold", color: "success.main" }}>
                    {formatCurrency(Number(factura?.importeTotal), monedaFactura)}
                  </Typography>
                </Box>
                
                {/* Método de pago */}
                <FormControl fullWidth error={!!errorMetodoPago}>
                  <InputLabel id="metodo-pago-label">Método de pago*</InputLabel>
                  <Select
                    labelId="metodo-pago-label"
                    label="Método de pago*"
                    value={metodoPago?.nombre || ""}
                    onChange={(e) => {
                      const nombre = e.target.value;
                      const codigo = metodosPago.find((m) => m.nombre === nombre)?.codigo!;
                      setMetodoPago({ nombre, codigo, fechaPago: moment() });
                      setErrorMetodoPago(undefined);
                    }}
                  >
                    {metodosPago
                      .filter((m) => m.activo)
                      .map((m) => (
                        <MenuItem key={m.codigo} value={m.nombre}>
                          {m.nombre}
                        </MenuItem>
                      ))}
                  </Select>
                    
                  {errorMetodoPago && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                      {errorMetodoPago}
                    </Typography>
                  )}
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Fecha de pago"
                    value={metodoPago?.fechaPago}
                    onChange={(date) => {
                      if (metodoPago) {
                        setMetodoPago({ ...metodoPago, fechaPago: date ?? moment() });
                        setErrorFechaPago(undefined);
                      }
                    }}
                    renderInput={(params) => <TextField required fullWidth sx={{ mt: 2 }} error={!!errorFechaPago} helperText={errorFechaPago} {...params} />}
                  />
                </LocalizationProvider>
              </DialogContent>              
              <DialogActions sx={{ p: 2 }}>
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={pagarFactura}
                  disabled={loadingPagar}
                  startIcon={loadingPagar && <CircularProgress size={18} />}
                >
                  Pagar
                </Button>
                  
                <Button 
                  variant="outlined" 
                  color="inherit"
                  onClick={closeModalPagar}
                  disabled={loadingPagar}
                >
                  Cancelar
                </Button>
              </DialogActions>
            </Dialog>}

            {/* Modal emitir */}
            {modalEmitir && 
            <Dialog open={modalEmitir} onClose={loadingEmitir ? undefined : closeModalEmitir} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: "bold" }}>Emitir factura</DialogTitle>
              <DialogContent sx={{ pt: 1 }}>
                {erroresPaciente.length > 0 ? (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    No se puede emitir la factura. Faltan datos importantes:
                    <ul>
                      {erroresPaciente.map((e) => (
                        <li key={e}>{e}</li>
                      ))}
                    </ul>
                  </Alert>
                ) : 
                <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {!factura?.receptor?.direccion && (
                    <>La factura se emitirá sin dirección del paciente.<br /><br /></>
                  )}
                  Una vez emitida, la factura no se podrá modificar.
                  Solo se podrá generar una factura rectificativa.
                </Alert>
    
                <NumeroOrientativoEmision info={numeroOrientativo} />

    
                <Typography>
                  ¿Deseas emitir esta factura definitivamente?
                </Typography>
                </>
                }
    
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                {erroresPaciente.length === 0 ? (
                  <>
                    <Button 
                      variant="contained" 
                      color="success"
                      onClick={emitirFactura}
                      disabled={loadingEmitir}
                      startIcon={loadingEmitir && <CircularProgress size={18} />}
                    >
                      Emitir
                    </Button>
                
                    <Button 
                      variant="outlined" 
                      color="inherit"
                      onClick={closeModalEmitir}
                      disabled={loadingEmitir}
                    >
                      Cancelar
                    </Button>
                  </>
                ) : (
                  <Button 
                    variant="outlined" 
                    color="inherit"
                    onClick={closeModalEmitir}
                  >
                    Cerrar
                  </Button>
                )}
              </DialogActions>
            </Dialog>}

            {/* Modal rectificar factura manual */}
            {facturaRectificar !== null &&
            <RectificarFacturaManual 
              factura={facturaRectificar}
              pacienteId={paciente?._id!}
              open={!!facturaRectificar}
              onClose={() => setFacturaRectificar(null)}
              onFacturaRectificada={(facturaRectificativa: IFacturaCompleta) => {
                setFactura(prev => {
                  if (!prev) return prev;
                
                  return {
                    ...prev,
                    facturaRectificada: facturaRectificativa.numero,
                    facturaRectificadaId: facturaRectificativa._id
                  };
                });
                if (onFacturaRectificada) onFacturaRectificada(facturaRectificativa);
              }}
            />
            }

            {/* Modal cambiar entidad */}
            {modalCambiarEntidad && (
              <CambiarEntidadDialog
                open={modalCambiarEntidad}
                onClose={() => setModalCambiarEntidad(false)}
                entidadActualId={factura?.entityId ?? null}
                esBorrador={factura?.estado === "Borrador"}
                loading={loadingCambiarEntidad}
                onConfirm={handleCambiarEntidad}
              />
            )}

            {/* Modal anular */}
            {modalAnular &&
            <AnularFacturaDialog
              open={modalAnular}
              loading={loadingAnular}
              numero={factura?.numero}
              esBorrador={factura?.estado === "Borrador"}
              esRectificativa={!!factura?.facturaOriginalId}
              motivo={motivoAnulacion}
              onMotivoChange={setMotivoAnulacion}
              confirmar={confirmarAnulacion}
              onConfirmarChange={setConfirmarAnulacion}
              onClose={() => setModalAnular(false)}
              onConfirm={handleAnularFactura}
            />}

            <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
              <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
                {alert.msg}
              </Alert>
            </Snackbar>
        </>
    );
};

export default DetalleFacturaManual;